import React, { useState } from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormLabel from "@mui/material/FormLabel";
import type { StandardTextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import SVG from "@/assets/svg";

export interface IInput extends StandardTextFieldProps {
  label?: React.ReactNode;
  pattern?: RegExp;
  value?: string | number;
  errorText?: any;
  placeholder?: string;
  setOpen?: (value: boolean) => void;
  id?: string;
  helperText?: string;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}

const InputTypes = {
  PASSWORD: "password",
  AUTOCOMPLETE: "autocomplete",
  PERCENT: "percent",
};

function LabeledInputComponent({
  label,
  type,
  pattern,
  value,
  errorText,
  placeholder,
  setOpen,
  id,
  slotProps,
  sx,
  helperText,
  step,
  min,
  max,
  required,
  // onChange,
  ...rest
}: IInput) {
  const [showPassword, setShowPassword] = useState(false);
  const generatedId = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const inputId = id || `input-${generatedId}`;

  const isError: boolean =
    pattern && value !== undefined && value !== null && value !== ""
      ? !pattern.test(String(value))
      : false;

  const numberInputOnWheelPreventChange = (e: any) => {
    // Prevent the input value change
    e.target.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current function is done)
    setTimeout(() => {
      e.target.focus();
    }, 0);
  };

  const endAdornment =
    type === InputTypes.PASSWORD ? (
      showPassword ? (
        <SVG.EyeOpen
          width="25"
          className="eyaIcon"
          sx={{ cursor: "pointer" }}
          onClick={() => setShowPassword(!showPassword)}
        />
      ) : (
        <SVG.Eye
          className="eyaIcon"
          sx={{ cursor: "pointer" }}
          onClick={() => setShowPassword(!showPassword)}
        />
      )
    ) : type === InputTypes.AUTOCOMPLETE ? (
      <SVG.Search />
    ) : type === InputTypes.PERCENT ? (
      <Typography
        sx={{
          color: "#1F4970",
          fontSize: "18px",
          fontWeight: "600",
        }}
      >
        %
      </Typography>
    ) : undefined;

  return (
    <>
      {label && (
        <FormLabel
          htmlFor={inputId}
          sx={{
            color: "#1F4970",
            fontSize: "14px",
            fontWeight: "600",
            padding: "12px 4px",
            // textTransform: " capitalize",
          }}
        >
          {label}
        </FormLabel>
      )}
      <ClickAwayListener onClickAway={() => setOpen && setOpen(false)}>
        <TextField
          placeholder={placeholder}
          type={
            type === "password"
              ? showPassword
                ? "text"
                : "password"
              : type || "text"
          }
          onKeyDown={(e) => {
            if (type === "number") {
              if (e.code === "KeyE" || e.key === "-" || e.key === "+") {
                e.preventDefault();
              }
            }
          }}
          onWheel={(e) => {
            if (type === "number") {
              numberInputOnWheelPreventChange(e);
            }
          }}
          {...rest}
          value={value}
          required={false}
          error={isError || !!errorText}
          helperText={isError || errorText ? errorText : helperText}
          sx={[
            {
              "& .MuiOutlinedInput-root": {
                borderColor: isError || errorText ? "#FF0000" : "",
                color: isError || errorText ? "#FF0000" : "#000",
                fontSize: "0.875rem",
                rowGap: "10px",
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: "#000000",
                fontWeight: "500",
                opacity: 0.7,
              },
              "& .MuiSelect-select.Mui-disabled": {
                WebkitTextFillColor: "#000000 !important",
                color: "#000000 !important",
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#000000 !important",
                color: "#000000 !important",
              },
              "& .MuiInputBase-input:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 100px #fff inset !important",
                WebkitTextFillColor: "#000000 !important",
                transition: "background-color 5000s ease-in-out 0s",
                fontFamily: "inherit",
                fontSize: "inherit",
              },
              "& .MuiInputBase-input:-webkit-autofill:hover": {
                WebkitBoxShadow: "0 0 0 100px #fff inset !important",
                WebkitTextFillColor: "#000000 !important",
                transition: "background-color 5000s ease-in-out 0s",
              },
              "& .MuiInputBase-input:-webkit-autofill:focus": {
                WebkitBoxShadow: "0 0 0 100px #fff inset !important",
                WebkitTextFillColor: "#000000 !important",
                transition: "background-color 5000s ease-in-out 0s",
              },
              "& .MuiInputBase-input:-webkit-autofill:active": {
                WebkitBoxShadow: "0 0 0 100px #fff inset !important",
                WebkitTextFillColor: "#000000 !important",
                transition: "background-color 5000s ease-in-out 0s",
              },
              "& input::-webkit-contacts-auto-fill-button, & input::-webkit-credentials-auto-fill-button":
                {
                  visibility: "hidden",
                  display: "none !important",
                  pointerEvents: "none",
                  position: "absolute",
                  right: 0,
                },
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/^\s+/, "");

            rest?.onChange?.(e);
          }}
          slotProps={{
            ...slotProps,
            htmlInput: (ownerState) => {
              const resolvedProps =
                typeof slotProps?.htmlInput === "function"
                  ? slotProps.htmlInput(ownerState)
                  : slotProps?.htmlInput;

              return {
                ...resolvedProps,
                id: inputId,
                ...(step !== undefined ? { step } : {}),
                ...(min !== undefined ? { min } : {}),
                ...(max !== undefined ? { max } : {}),
              };
            },
            input: (ownerState) => {
              const resolvedProps =
                typeof slotProps?.input === "function"
                  ? slotProps.input(ownerState)
                  : slotProps?.input;

              return {
                ...resolvedProps,
                ...(!resolvedProps?.endAdornment &&
                  endAdornment && { endAdornment }),
              };
            },
          }}
        />
      </ClickAwayListener>
    </>
  );
}

export default LabeledInputComponent;
